import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {DoppeDtoStoreProduct} from '../../client-server-common';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    doppeDtoProductGetMediaItems,
    doppeDtoStoreProductPriceDataConvertToActionPrice,
    DoppeExternalProductProvider,
    doppeExternalProductProviderResolveBusinessEntityReference
} from '../../client-server-common';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import {arrayRemoveNullValues, asArray, immerProduce, RichTextContent, HideableValue, hideableValueCreateHidden, hideableValueGetValue, hideableValueGetVisibleValue, hideableValueIsVisibleAndNotEqualValue } from '@wix/devzai-utils-common';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import {
    doppeActionCtaButtonsResetSettingsForLock,
    DoppeActionCTAType,
    DoppeActionCTATypeLinkAutoSource,
    DoppeActionCTATypeLinkOpenType,
    doppeActionMultipleCTAButtonCreate, DoppeLinkSpecEntityType, DoppeLinkSpecType
} from '../../client-server-common/types/doppe-action-cta-button';
import {autoValueCreate} from '@wix/devzai-utils-common';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionPrice} from '../../client-server-common/types/doppe-action-price';
import type {DoppeActionMediaViewSettings} from '../../client-server-common/types/doppe-action-media-view-settings';
import {
    doppeActionGetMediaViewDefaultSettingsMigration
} from '../../client-server-common/types/doppe-action-media-view-settings';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';

export namespace DoppeActionTypeStoreProduct {

    export interface BaseActionSettings extends
        DoppeActionMediaViewSettings {
        price: DoppeActionPrice
        productTitle: string;
        description: RichTextContent;
        productImage: WixMediaResource[] | null;
        couponCode: HideableValue<string>;
        ribbon: string;
        productProvider: DoppeExternalProductProvider | null;
        productProviderId: string | null;
    }

    export interface ActionSettings extends
        BaseActionSettings,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings {
    }
}

export const doppeActionTypeStoreProduct = doppeActionTypeDefineMetadata<DoppeActionTypeStoreProduct.ActionSettings>({
    id: 'io.bymo.action/store-product',
    resolveActionSettings: action => {

        const {
            ctaButtons,
            mainMediaViewSettings,
            mainMediaViewLayout,
            ...restSettings
        } = action.settings;

        return {
            productTitle: (''),
            description: (''),
            productImage: null,
            couponCode: hideableValueCreateHidden('SALE30'),
            ribbon: ('SALE'),
            productProvider: DoppeExternalProductProvider.Hopp,
            productProviderId: null,
            price: ({
                actualPrice: 100,
                currencyCode: 'USD',
            }),
            ...doppeActionGetMediaViewDefaultSettingsMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration({
                ctaButtons: ctaButtons,
            }, false),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...restSettings
        }
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            productTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            productImage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
            couponCode: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityCouponCode),
            ribbon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityRibbon)
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {
                [DoppeActionCTAType.BuyNow]: true
            }
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
            couponCode: true,
        }
    },
    actionListItemSettingsMetadata: false,
    isWidgetOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    resolveUsedMedia: actionSettings => {
        return arrayRemoveNullValues(asArray(hideableValueGetVisibleValue(actionSettings.productImage, []) ?? []));
    },
    resolveMainMedia: actionSettings => {
        const mediaItems = arrayRemoveNullValues(asArray(hideableValueGetVisibleValue(actionSettings.productImage, []) ?? []));
        return mediaItems[0] ?? null;
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: actionSettings.productProviderId ? {
                type: DoppeLinkSpecType.Entity,
                entityType: DoppeLinkSpecEntityType.Product,
                entityId: actionSettings.productProviderId
            } : null,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? null;
    },
    resolveCouponData: actionSettings => {
        return {
            showCouponCode: hideableValueIsVisibleAndNotEqualValue(actionSettings.couponCode, ''),
            couponCode: hideableValueGetValue(actionSettings.couponCode),
            couponDescription: '',
            couponTitle: hideableValueGetVisibleValue(actionSettings.productTitle, ''),
        }
    },
    resolveUsedBusinessEntities: actionSettings => {
        return arrayRemoveNullValues([
            actionSettings.productProvider !== null && actionSettings.productProviderId !== null ?
                doppeExternalProductProviderResolveBusinessEntityReference(
                    actionSettings.productProvider,
                    actionSettings.productProviderId
                ) :
                null
        ])
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            if (actionSettings.productImage) {
                const images = actionSettings.productImage ?? [];
                actionSettings.productImage = images.map(image => {
                    return wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap)
                })
            }
        })
    },
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
});

export function doppeActionTypeStoreProductResolveSettingsFromBoundEntity(
    actionSettings: DoppeActionTypeStoreProduct.BaseActionSettings,
    storeProduct: DoppeDtoStoreProduct
): DoppeActionTypeStoreProduct.BaseActionSettings {

    const productImages = doppeDtoProductGetMediaItems(storeProduct);

    return {
        ...actionSettings,
        productImage: productImages ?? actionSettings.productImage,
        price: doppeDtoStoreProductPriceDataConvertToActionPrice(storeProduct.price) ?? actionSettings.price,
        productTitle: (storeProduct.name ?? actionSettings.productTitle),
        description: (storeProduct.descriptionPlainText ?? actionSettings.description),
        ribbon: storeProduct.ribbon ?? actionSettings.ribbon,
    }
}

export function convertDoppeWixProductToStoreProductSettings(options: {
    storeProduct: DoppeDtoStoreProduct, productSettings?: DoppeActionTypeStoreProduct.ActionSettings, productProvider?: DoppeExternalProductProvider
}) {

    const {
        storeProduct,
        productSettings = {},
        productProvider = DoppeExternalProductProvider.Hopp,
    } = options;

    const productImages = doppeDtoProductGetMediaItems(storeProduct)

    const productTitle = storeProduct?.name;

    return {
        couponCode: hideableValueCreateHidden(''),
        ...doppeActionCtaButtonsSettingsDefaultValuesMigration({
            ctaButtons: [doppeActionMultipleCTAButtonCreate({
                text: '',
                isRequired: true,
                settings: {
                    type: DoppeActionCTAType.Link,
                    url: autoValueCreate(DoppeActionCTATypeLinkAutoSource.SameAsSourceURL),
                    openType: DoppeActionCTATypeLinkOpenType.SameTab
                }
            })],
        }, false),
        ...productSettings,
        ...doppeActionGetMediaViewDefaultSettingsMigration(productSettings),
        ...doppeActionContentSectionSettingsDefaultValuesMigration(productSettings),
        price: (doppeDtoStoreProductPriceDataConvertToActionPrice(storeProduct.price)),
        ribbon: (storeProduct.ribbon ?? ''),
        productImage: (productImages),
        productTitle: (productTitle ?? ''),
        description: (storeProduct.descriptionPlainText),
        productProvider: productProvider,
        productProviderId: storeProduct.id,
    } satisfies DoppeActionTypeStoreProduct.ActionSettings
}